import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { useLanguages } from '@/hooks/use-languages';
import { getDefaultLanguages } from '@/lib/language.utils';
import { safePlay } from '@/lib/audio-playback';
import { transcriptionApi } from '@/api/transcription.api';
import { transcriptionSourceOf, type TranscriptionSource } from '@/lib/transcription-source';
import { useSessionDeepLink } from '@/hooks/use-session-deeplink';
import { useAudioWaveform } from '@/features/contribution-kit';
import { useValidationGate, isCrossValidationBlockedError } from '@/features/validation-gate';
import type { ValidationGate } from '@/types/auth.types';
import type { AudioEntry, TranscriptionAudioSessionData } from './types';
import { normalizeAudioItems, extractTotal, mapLanguageToCode, filterAudioItemsByLanguage } from './helpers';
import {
  hasMoreAudios,
  nextAudioStep,
  pendingAudiosQuery,
  releasePayload,
  rotationIdOf,
  submitBlocker,
  transcriptionPayload,
} from './transcription-audio.session';

/**
 * State and actions of a "Transcrire" session: rotating batches of pending
 * audios, the transcription and optional French text, submit / skip /
 * previous, validation gate and the `?session=transcription` deep link.
 * The two arguments are kept for the page's existing contract.
 */
export function useTranscriptionAudioSession(
  _fromSpeakContributeData: TranscriptionAudioSessionData,
  _locationState: unknown,
) {
  const { t, user, theme, language } = useApp();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeLanguages, isLoading: isLoadingLanguages } = useLanguages();
  const validationGate = useValidationGate();
  const [blockedGate, setBlockedGate] = useState<ValidationGate | null>(null);
  const clearBlockedGate = useCallback(() => setBlockedGate(null), []);

  const languages = activeLanguages.length > 0 ? activeLanguages : getDefaultLanguages();

  const currentLang = useMemo(() => {
    const param = window.location.pathname.split('/')[1];
    return param || language || 'fr';
  }, [language]);

  const [selectedLanguage, setSelectedLanguage] = useState('');
  // In the URL, so a link can open the initial-data answers queue directly.
  const source = transcriptionSourceOf(searchParams.get('source'));
  const setSource = useCallback(
    (next: TranscriptionSource) =>
      setSearchParams((current) => {
        const params = new URLSearchParams(current);
        if (next === 'all') params.delete('source');
        else params.set('source', next);
        return params;
      }, { replace: true }),
    [setSearchParams],
  );
  const queueKey = `${selectedLanguage}|${source}`;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [audios, setAudios] = useState<AudioEntry[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [text, setText] = useState('');
  const [textFrancais, setTextFrancais] = useState('');
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const loadRequestIdRef = useRef(0);
  const previousLanguageRef = useRef('');
  const rotationIdRef = useRef<string | null>(null);
  const seenAudioIdsRef = useRef<Set<string>>(new Set());
  const submittedAudioIdsRef = useRef<Set<string>>(new Set());

  const audioRef = useRef<HTMLAudioElement>(null);
  const current = audios[currentIndex] || null;
  const { canvasRef, peaks } = useAudioWaveform({ audioRef, audioUrl: current?.audioUrl, theme });

  // A new language or source is a new queue: start over.
  useEffect(() => {
    if (!previousLanguageRef.current) {
      previousLanguageRef.current = queueKey;
      return;
    }
    if (previousLanguageRef.current === queueKey) return;

    previousLanguageRef.current = queueKey;
    loadRequestIdRef.current += 1;
    setSessionStarted(false);
    setSessionComplete(false);
    setSubmittedCount(0);
    setAudios([]);
    setCurrentIndex(0);
    setText('');
    setTextFrancais('');
    setPage(1);
    setTotal(null);
    setHasMore(false);
    setIsPlaying(false);
    rotationIdRef.current = null;
    seenAudioIdsRef.current.clear();
    submittedAudioIdsRef.current.clear();
  }, [queueKey]);

  const loadPage = useCallback(async (newPage: number) => {
    if (!selectedLanguage || !user?.id) return;
    const requestId = ++loadRequestIdRef.current;
    const code = mapLanguageToCode(selectedLanguage, languages);
    setIsLoading(true);
    try {
      const response = await transcriptionApi.getPendingAudios(pendingAudiosQuery({
        lang: code,
        seenIds: seenAudioIdsRef.current,
        submittedIds: submittedAudioIdsRef.current,
        rotationId: rotationIdRef.current,
        source,
      }));
      if (requestId !== loadRequestIdRef.current) return;

      const items = filterAudioItemsByLanguage(
        normalizeAudioItems(response as Record<string, unknown>, newPage),
        code,
        languages,
      );
      items.forEach((item) => seenAudioIdsRef.current.add(String(item.id)));
      const nextRotationId = rotationIdOf(response);
      if (nextRotationId !== null) rotationIdRef.current = nextRotationId;
      const totalFromApi = extractTotal(response as Record<string, unknown>);

      setAudios(items);
      setPage(newPage);
      setCurrentIndex(0);
      setTotal(totalFromApi);
      setHasMore(hasMoreAudios(totalFromApi, seenAudioIdsRef.current.size, items.length));

      if (!items.length) {
        toast({ title: 'Aucun audio disponible', description: 'Aucun nouvel audio n\'est actuellement disponible pour cette langue.' });
      }
    } catch (err: unknown) {
      if (requestId !== loadRequestIdRef.current) return;
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Erreur de chargement', description: message || 'Impossible de récupérer les audios', variant: 'destructive' });
    } finally {
      if (requestId === loadRequestIdRef.current) setIsLoading(false);
    }
  }, [selectedLanguage, source, user?.id, languages, toast]);

  const startSession = useCallback(async () => {
    if (!selectedLanguage) {
      toast({ title: 'Langue requise', description: 'Sélectionnez une langue avant de démarrer une session.', variant: 'destructive' });
      return;
    }
    if (!user?.id) {
      toast({ title: 'Connexion requise', description: 'Connectez-vous pour accéder aux tâches de transcription.', variant: 'destructive' });
      return;
    }
    rotationIdRef.current = null;
    seenAudioIdsRef.current.clear();
    submittedAudioIdsRef.current.clear();
    setSessionComplete(false);
    setSubmittedCount(0);
    await loadPage(1);
    setSessionStarted(true);
  }, [selectedLanguage, user?.id, loadPage, toast]);

  const { startLinkedSession, sessionResumeActive } = useSessionDeepLink({
    searchParams,
    setSearchParams,
    sessionValue: 'transcription',
    selectedLanguage,
    setSelectedLanguage,
    sessionStarted,
    isStartingSession: isLoading,
    startSession,
  });

  const playAudio = useCallback(() => {
    const element = audioRef.current;
    if (!element) return;
    if (isPlaying) {
      element.pause();
      setIsPlaying(false);
      return;
    }
    setIsPlaying(true);
    safePlay(element, { onError: () => setIsPlaying(false) });
  }, [isPlaying]);

  const onAudioEnded = useCallback(() => setIsPlaying(false), []);

  const selectAudio = useCallback((i: number) => {
    if (i === currentIndex) return;
    setIsPlaying(false);
    const a = audioRef.current;
    if (a) { a.pause(); a.currentTime = 0; }
    setCurrentIndex(i);
  }, [currentIndex]);

  const submitTranscription = useCallback(async () => {
    const blocker = submitBlocker({ hasCurrent: Boolean(current), text, isSubmitting, gateBlocked: validationGate.isBlocked });
    if (blocker === 'textRequired') {
      toast({ title: 'Texte requis', description: 'Veuillez écrire la transcription', variant: 'destructive' });
      return;
    }
    if (blocker === 'busy' || !current) return;
    if (blocker === 'gate') {
      setBlockedGate(validationGate.gate ?? null);
      return;
    }

    setIsSubmitting(true);
    try {
      await transcriptionApi.add(transcriptionPayload(current.id, text, textFrancais));
      submittedAudioIdsRef.current.add(String(current.id));
      setSubmittedCount((count) => count + 1);
      toast({ title: 'Transcription ajoutée', description: 'Votre contribution a été enregistrée avec succès!' });
      setText('');
      setTextFrancais('');
      const step = nextAudioStep(currentIndex, audios.length, hasMore);
      if (step === 'advance') {
        setCurrentIndex((i) => i + 1);
      } else if (step === 'nextBatch') {
        await loadPage(page + 1);
      } else {
        toast({ title: t.sessionEnded });
        setSessionComplete(true);
      }
    } catch (err: unknown) {
      const blockedFromError = isCrossValidationBlockedError(err);
      if (blockedFromError) {
        validationGate.setGateFromError(blockedFromError);
        setBlockedGate(blockedFromError);
        return;
      }
      const message = isAxiosError(err)
        ? err.response?.data?.message ?? err.message
        : err instanceof Error
          ? err.message
          : String(err);
      toast({ title: 'Erreur', description: message || 'Échec de l\'envoi', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  }, [current, text, textFrancais, currentIndex, audios.length, hasMore, page, loadPage, toast, t, isSubmitting, validationGate]);

  const previous = useCallback(() => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  }, [currentIndex]);

  const skipAudio = useCallback(async () => {
    setText('');
    setTextFrancais('');

    if (current) {
      try {
        await transcriptionApi.release(releasePayload(current.id, rotationIdRef.current));
      } catch {
        // The reservation has a server-side expiry; skipping must remain usable
        // even when the release request is interrupted.
      }
    }

    const step = nextAudioStep(currentIndex, audios.length, hasMore);
    if (step === 'advance') {
      setCurrentIndex((i) => i + 1);
      toast({
        title: String(t.audioskipped ?? 'Audio passé'),
        description: String(t.audioskippedDesc ?? 'Passé au prochain audio'),
      });
    } else if (step === 'nextBatch') {
      await loadPage(page + 1);
      toast({
        title: String(t.audioskipped ?? 'Audio passé'),
        description: String(t.loadingNewAudios ?? 'Chargement de nouveaux audios...'),
      });
    } else {
      toast({
        title: String(t.noMoreAudios ?? 'Plus d\'audios'),
        description: String(t.noMoreAudiosDesc ?? 'Aucun autre audio disponible pour le moment'),
      });
    }
  }, [current, currentIndex, audios.length, hasMore, page, loadPage, toast, t]);

  const nextPage = useCallback(async () => {
    if (hasMore && !isLoading) await loadPage(page + 1);
  }, [hasMore, isLoading, loadPage, page]);

  const dismissSessionComplete = useCallback(() => setSessionComplete(false), []);

  return {
    selectedLanguage, setSelectedLanguage,
    source, setSource,
    sessionStarted, setSessionStarted,
    sessionComplete, dismissSessionComplete,
    submittedCount,
    sessionResumeActive,
    isLoading,
    isLoadingLanguages,
    isSubmitting,
    audios, currentIndex, setCurrentIndex,
    isPlaying, setIsPlaying,
    text, setText,
    textFrancais, setTextFrancais,
    showKeyboard, setShowKeyboard,
    page, total, hasMore,
    current,
    audioRef, canvasRef, peaks,
    currentLang,
    languages,
    validationGate,
    blockedGate,
    clearBlockedGate,
    progressPercent: audios.length > 0 ? Math.round((currentIndex / audios.length) * 100) : 0,
    startLinkedSession,
    loadPage,
    playAudio,
    onAudioEnded,
    selectAudio,
    submitTranscription,
    skipAudio,
    previous,
    nextPage,
  };
}
